/**
 * Table classification: control vs tenant (V98-7-5 / ADR-0018, then V115-3-2 / ADR-0107).
 *
 * Since V115-3-2 every environment has ONE database (ADR-0107 §2) and nothing routes by plane: the
 * plane router (`PlaneRoutedDatabase`) is retired. The classification stays because it still says
 * which tables hold a workspace's own records:
 *   - CONTROL tables: identity, tenancy, membership, billing, cross-tenant links, the audit-firm
 *     roster, and the global content catalog every tenant reads (frameworks, controls, rules, research);
 *   - TENANT tables: the workspace's own governance records. Every statement on one that carries
 *     `workspace_id` must filter by it, or be a reviewed exception
 *     (`scripts/check-tenant-predicates.mjs`); row-level security covers the same list (V115-3-7).
 *
 * The lists cover every table and view in db/schema.sql exactly once
 * (`data-plane-router.test.ts` enforces it), so a new table must be classified when it is added. The
 * firm tables are spread from the firm-tier repository's own lists (ADR-0047 R3 — this file never
 * names one).
 */
import { FIRM_CONTROL_TABLES, FIRM_TENANT_TABLES } from './firm-tier-repository.js';
/** Control tables: identity, tenancy, billing, the firm roster, the global catalog. */
export const CONTROL_PLANE_TABLES = [
    // identity + tenancy + auth path
    'users', 'organizations', 'org_memberships', 'workspaces', 'memberships', 'invitations',
    'domain_claims', 'join_requests', 'pending_domain_users', 'assessor_engagements',
    'workspace_api_tokens', 'workspace_ci_issuers', 'workspace_ci_verification_identities', 'workspace_rate_limits',
    // ADR-0055 / migration 284: a signed collector push resolves its workspace from these
    'customer_collectors', 'collector_registration_codes', 'collector_push_nonces',
    // ADR-0062: Azure consent requests + tenant bindings (resolved before any workspace context).
    'azure_consent_requests', 'azure_tenant_bindings',
    // billing
    'plans', 'subscriptions', 'billing_events',
    // cross-tenant / org-scoped links (rows name two workspaces or an org)
    'supplier_grants', 'register_subscriptions', 'release_acknowledgements',
    'entry_review_requests', 'supplier_disclosure_requests', 'org_ai_risk_bars',
    // global content catalog (every tenant reads the same rows)
    'risk_items', 'frameworks', 'editors', 'framework_metadata', 'framework_mappings',
    'framework_requirements', 'legislation', 'case_law', 'breach_events', 'service_classifications',
    'controls', 'control_edges', 'control_specs', 'readiness_outcomes', 'readiness_methodology_versions',
    'certification_timeframes', 'jurisdiction_rules', 'dpa_intervention_rules', 'rules_snapshots',
    'standard_versions', 'reference_architecture_versions', 'cloud_target_mappings',
    'questionnaire_templates', 'collectors', 'vendors', 'vendor_coverage',
    // migration 275 / ADR-0051: connector kind registry (seeded on both planes by the migration)
    'connector_kinds', 'connector_auth_kinds',
    // migration 273a / ADR-0050: program registry (seeded on both planes by the migration)
    'programs',
    // migrations 268a / 290 / ADR-0056: assessment content (objectives, tests, versions, maps)
    'assessment_objectives', 'assessment_tests', 'assessment_test_versions', 'test_objective_map',
    // migration 266a / ADR-0058: versioned program rule sets (CMMC points, thresholds, clocks)
    'program_rule_sets',
    // v11.3 / ADR-0101 §6 (migration 804): versioned monitoring rule sets (system class, prescription, vocabulary, candidates)
    'monitoring_rule_sets',
    // migration 742 / V111-7-5: variant-detector thresholds (versioned content, one set for every tenant)
    'variant_match_settings',
    // V112-4-1 (ADR-0098): versioned thresholds of the legal-finding control-impact analysis.
    'control_impact_settings',
    // V112-5-1 / ADR-0116 (migration 793): vocabulary terms added through the review queue.
    'vocabulary_terms',
    // v11.8 / ADR-0117 (1050–1052): obligation-record and intake lookups; one immutable obligation set per statute text.
    'deontic_kinds', 'binds_whom_scopes', 'actor_classes', 'intake_question_kinds', 'intake_package_statuses',
    'statute_obligation_sets',
    // migration 322a / ADR-0064: 800-53 baselines (GovRAMP Moderate, FedRAMP CTL) and their members
    'control_baselines', 'baseline_members',
    // migration 342a / ADR-0069 §2: provider inheritance catalog (reviewed content)
    'provider_inheritance_catalog', 'provider_inheritance_objectives',
    // migrations 450 / 452 / 453 / ADR-0075: FedRAMP + GovRAMP status data, coverage map, package corpus (reviewed content)
    'marketplace_snapshots', 'fedramp_offerings', 'fedramp_offering_services', 'fedramp_status_events', 'provider_product_services',
    'govramp_products', 'fedramp_corpus_packages', 'fedramp_corpus_tpr_entries',
    // migration 463 / ADR-0075 §6: the substitution catalog (reviewed content)
    'substitution_catalog_entries',
    // v10.5 / ADR-0074 O1 (migration 466b): reviewed IaC templates (global content)
    'iac_templates',
    // V105-7-2 / ADR-0074 owner decision 2026-10-09 (migration 470): reviewed spike definitions (global content)
    'spike_definitions',
    // migration 298 / ADR-0059: C3PAO firm-type join (control plane, alongside the firm roster).
    'audit_firm_types',
    // migration 335b / ADR-0067: assessor firm families (reference rows, seeded on both planes).
    'assessor_firm_types',
    'research_sources', 'research_runs', 'research_findings', 'proposed_changes', 'source_blocklist',
    'propagation_events', 'scheduler_ticks', 'pilot_signups',
    // migrations 410 / 411 / ADR-0076: statute texts + needs-authoring items (editorial pipeline, global content)
    'statute_texts', 'rule_authoring_items',
    // migration 412 / ADR-0077: jurisdiction role / tier terms (reviewed global content)
    'jurisdiction_terms',
    // v10.9 Track 3 / ADR-0090 (migrations 658–659): check-library sources + catalog releases (reviewed content)
    'source_benchmarks', 'source_search_records', 'catalog_releases',
    // v10.9 Track 3 / ADR-0088 §3 (migrations 660–661, 663): declarative rules, versions, release membership, references
    'check_rules', 'check_rule_versions', 'catalog_release_rules', 'check_rule_references',
    // BL-42 / ADR-0097 (migration 370): the release ledger, written only by scripts/release/migrations.mjs apply
    'release_migrations',
    // v11.5 / ADR-0109 §5 (migration 915): one row per retention run per raw table, written only by the retention job
    'retention_runs',
    // v11.7 / ADR-0111 §9 (migrations 1003, 1006, 1007): the legal source record's lookups, documents, jurisdiction
    // registry and legislative sessions (reviewed global content, written only through the review queue)
    'jurisdiction_levels', 'legal_statuses', 'source_kinds', 'source_doc_roles', 'jurisdiction_source_roles',
    'source_scope_groups', 'legislation_topics',
    'source_documents', 'jurisdiction_sources', 'legislative_sessions',
    // v11.5 / ADR-0108 (migration 905): the nine growing value lists as lookup tables (data/value-lists.ts)
    'proposal_target_types', 'finding_kinds', 'certification_package_kinds', 'notification_types',
    'research_source_kinds', 'research_workflow_kinds', 'control_families', 'assignment_subject_kinds',
    'monitoring_rule_set_families',
    // v11.6 / ADR-0113 (953, 954): court cases as sources — global editorial content
    'case_weights', 'case_link_effects', 'case_control_links',
    // v11.7 Track 3 / ADR-0112 §6 (migration 1020): reviewed control guides (platform content, one active per control)
    'control_guides',
    // v11.7 Track 3 / ADR-0112 §5 (migration 1022): per-program floor-fix switches (owner-set, default OFF)
    'program_floor_switches',
    // ADR-0047: the audit-firm roster
    ...FIRM_CONTROL_TABLES,
];
/** Tenant plane (the workspace's `data_plane`). */
export const TENANT_PLANE_TABLES = [
    // v11.7 / ADR-0111 §7 (migration 1005): a workspace's choice whether a contested law's rule applies (tenant data)
    'workspace_rule_applicability',
    // migrations 290 / 292 / 293 / ADR-0056 / ADR-0057: period test pins, determinations, assets (tenant data)
    'period_test_pins', 'determinations', 'assets', 'asset_period_memberships',
    // migration 321 / V102-3-4: per-viewer asset scope drafts (tenant data)
    'asset_scope_drafts',
    // migrations 270 / 271 / ADR-0058: CMMC affirmations and POA&M items (tenant data)
    'affirmation_records', 'poam_items', 'sprs_entries', 'customer_requirements', 'requirement_targets',
    // migration 329b / ADR-0064: GovRAMP ConMon / Snapshot cycles (tenant data)
    'conmon_cycles',
    // migration 298 / ADR-0059: C3PAO engagement roles + CMMC workpapers (tenant data; the SOC 2 / FedRAMP workpaper join from 243 is distinct)
    'assessor_engagement_roles', 'cmmc_workpapers',
    // migration 316 / ADR-0063: prime share link (tenant data; one row per shareable package + append-only access log)
    'prime_share_links', 'prime_share_link_access',
    // migration 338a / V103-6-4: FedRAMP Class A alternative-framework materials (tenant data)
    'alternative_framework_materials',
    // migration 509b / V106-1-7: SOC 2 issued-report records (tenant data)
    'soc2_reports',
    // migration 349a / V103-3-6: program events (GovRAMP grants / lapse notices, Ready booking, ConMon items, Class B bookings; tenant data)
    'program_events',
    // migration 529 / ADR-0080 §4: Statement of Applicability versions + entries (tenant data)
    'soa_versions', 'soa_entries',
    // migration 531a / ADR-0080 §5: certification-cycle milestones (tenant data)
    'program_cycle_milestones',
    // migration 542 / ADR-0078: program entries (tickets, risk requests, asks, off-platform CB results; tenant data)
    'program_entries',
    // migrations 294–297 / ADR-0060: evidence-strategy engines (tenant data)
    'reconciliation_runs', 'reconciliation_mismatches', 'sample_item_results', 'document_objective_links',
    'interview_records', 'responsibility_assignments',
    // migration 330b / ADR-0065: per-program control records + statement revisions (tenant data)
    'program_controls', 'program_control_revisions',
    // migration 278 / ADR-0052: per-connector event receipts (tenant data)
    'connector_events',
    // migration 281 / ADR-0052 §3: AI usage aggregates (tenant data)
    'ai_usage_rollups',
    // migration 288 / V101-1-9: dismissed provider suggestions (tenant data)
    'provider_suggestion_dismissals',
    // migration 455 / ADR-0073 §2: raise-only prompt-field class overrides (tenant data)
    'workspace_field_class_overrides',
    // migration 456 / ADR-0073 §6: customer-owned AI providers, their sealed keys and eval results (tenant data)
    'workspace_ai_providers', 'ai_provider_credentials', 'ai_provider_task_evals', 'ai_provider_owner_acks',
    // migration 283 / ADR-0054: remediation-coach drafts (tenant data)
    'finding_remediation_drafts',
    // profile / register / evidence
    'customer_profiles', 'customer_register_items', 'assessment_scores', 'assessments_history', 'status_changes',
    'evidence_artifacts', 'evidence_bindings', 'evidence_attestations', 'evidence_nudges', 'evidence_requests',
    'item_signals', 'item_checklist_steps', 'register_item_comments', 'assignment_history',
    'policy_drafts', 'questionnaires', 'questionnaire_questions', 'questionnaire_answers',
    'workspace_accreditation_targets', 'workspace_settings', 'workspace_ai_policy', 'workspace_user_attributes',
    'workspace_ai_risk_bars', 'workspace_delta_rule_levels', 'workspace_signing_settings',
    // signing + audit trail
    'hmac_keys', 'assessment_packages', 'audit_events', 'litigation_holds', 'sigstore_trust_roots',
    'signature_routing', 'release_signatures',
    // notifications / alerts / webhooks
    'alerts', 'alert_subscriptions', 'alert_user_state', 'notifications', 'alert_forwards', 'alert_forward_views',
    'webhook_endpoints', 'webhook_deliveries',
    // assessor Q&A (content of an engagement; the engagement row itself is control)
    'qa_threads', 'qa_messages', 'assessor_annotations', 'assessor_annotation_disputes',
    // AI systems, use cases, discovery, connectors, checks
    'ai_systems', 'ai_system_classifications', 'ai_use_cases', 'ai_use_case_reviews',
    'discovered_tools', 'discovery_signal_aggregates', 'collector_runs', 'ci_check_submissions',
    'evidence_connectors', 'connector_credentials', 'control_checks', 'control_spec_overrides', 'monitoring_bindings',
    'findings', 'finding_events', 'supplier_portfolio', 'risk_acceptances', 'ai_incidents', 'security_risks',
    'ai_disclosures', 'ai_disclosure_versions', 'register_releases', 'register_release_systems',
    'entry_review_states', 'entry_signatures', 'reporting_profiles',
    'ai_system_repos', 'ai_system_bom_snapshots', 'ai_component_catalog', 'ai_system_components', 'observed_ai_resources', 'system_ai_resource_dismissals',
    // V111-1 (ADR-0092): declared agent authority + observed authority facts.
    'ai_system_authority', 'observed_agent_authority',
    // V113-0 (ADR-0100): the configuration-item register, per-check attributions and monitoring methods.
    'ai_components', 'control_check_attributions', 'monitoring_methods',
    // v11.3 Track 1 / ADR-0101 §4 (migration 809): a member's acceptance of a proposed method (binding ↔ method link)
    'monitoring_binding_acceptances',
    // V113-3-1 (migration 815): the drift timeline index written by bindOnCheck.
    'binding_check_history',
    // v11.5 / ADR-0109 §2 (migration 915): daily roll-ups per binding, kept after raw rows expire
    'monitoring_daily_rollups',
    // v11.3 Track 2 / ADR-0102 §4 (migration 811): per-system anomaly-rule overrides set by an admin
    'anomaly_threshold_overrides',
    // V111-3 (ADR-0093): persisted incident notification clocks.
    'ai_incident_obligations',
    // V111-4 (ADR-0093): FAIR-lite scenario definitions + immutable runs.
    'risk_scenarios', 'risk_scenario_runs',
    // V111-5 (ADR-0094): the signed insurability pack.
    'insurability_packs', 'insurability_pack_links', 'insurability_pack_link_sessions', 'insurability_pack_withdrawals',
    // V113-6-4 (migration 835): the signed per-system post-market monitoring plan.
    'monitoring_plan_packages',
    // V113-5 (ADR-0103, migrations 825 / 827): derived pack validity, feed verify links, signed monitoring status snapshots.
    'pack_validity_cache', 'cds_verify_links', 'monitoring_status_snapshots',
    // V114-1-3 (ADR-0104, migration 855): fulfillment gap PR reports — the only stored gap state.
    'fulfillment_gap_prs',
    // V114-2-4 (ADR-0106, migrations 862 / 863): outbound webhook endpoints + append-only delivery log.
    'outbound_webhook_endpoints', 'outbound_webhook_deliveries',
    'common_control_policies', 'system_control_states', 'system_dependencies', 'system_stage_overrides',
    'system_obligation_sets', 'system_obligations', 'system_data_lineage', 'agent_runs', 'onboarding_checklist',
    'blueprints', 'blueprint_versions', 'blueprint_inventory', 'blueprint_gaps', 'blueprint_roadmap_items',
    'policy_bundles', 'compiler_runs', 'emitted_cloud_artifacts', 'remediation_prs',
    // v9.6 certification operations
    'certification_profiles', 'readiness_snapshots', 'ksi_validation_runs', 'recurring_obligations', 'obligation_occurrences',
    'access_reviews', 'access_review_decisions', 'access_review_rows', 'access_review_row_approvals',
    'access_review_attestations', 'significant_changes', 'scn_notice_sends', 'accepted_weaknesses',
    'audit_periods', 'populations', 'population_items', 'samples', 'policy_versions', 'policy_acknowledgments',
    'subservice_orgs', 'cuecs', 'system_descriptions', 'certification_packages', 'certification_package_drafts',
    'package_prerequisites', 'cds_grants', 'cds_tokens', 'cds_access_log', 'cds_grant_events', 'cds_access_requests',
    // v9.8 ownership, programs, coverage, SoD
    'assignments', 'responsibility_assignment_log', 'work_items', 'work_item_links',
    'signature_requests', 'signature_request_events', 'reporting_lines', 'escalation_chains',
    'independent_parties', 'absences', 'delegations', 'coverage_asks', 'compensating_reviews',
    'sod_record_addenda', 'independent_checks', 'sod_events', 'boundary_memberships',
    'external_services', 'external_service_documents', 'obligation_source_decisions', 'ai_program_events',
    'applicability_determinations', 'determination_fact_questions', 'privilege_releases', 'privilege_release_items',
    'rule_case_law_links',
    // v10.3 / ADR-0066 (migration 333): authorization boundary below the system level
    'boundary_components', 'boundary_component_scope', 'boundary_interconnections', 'boundary_ports_protocols', 'boundary_period_snapshots',
    // v10.3 / ADR-0069 (migration 342a): transition guidance (one open transition per workspace)
    'boundary_transitions', 'transition_programs', 'transition_target_scope', 'transition_responsibility_proposals',
    'transition_plan_items', 'transition_change_notices', 'transition_drafts',
    // v10.3 / ADR-0066 A3 (migration 344): connector resources as boundary-component suggestions
    'boundary_resource_suggestions',
    // v10.5 / ADR-0072 (migrations 458–460): infrastructure inventory, egress, intake, verdict answers
    'infra_inventory_items', 'infra_scan_runs', 'infra_inventory_sources', 'infra_egress_observations', 'infra_intake_answers', 'infra_verdict_answers',
    // v10.5 / ADR-0072 §1 (migration 477): asynchronous repository scan jobs
    'infra_scan_jobs',
    // v10.5 / ADR-0072 §8 (migration 462): immutable verdict snapshots
    'infra_verdict_snapshots', 'infra_partition_confirmations',
    // v10.8 / ADR-0086 (migrations 605–606): per-repo scan mode, automatic scan requests
    'infra_repo_scan_settings', 'infra_scan_requests',
    // v10.8 / ADR-0085 §5, ADR-0086 §5 (migrations 609, 611): derived fact suggestions, environment change log
    'system_fact_suggestions', 'environment_changes',
    // v10.5 / ADR-0072 §6 (migration 465): strategy score runs + signed strategy decisions
    'infra_strategy_scores', 'infra_decision_records',
    // v10.5 / ADR-0072 §6 (migration 472): a person's cost-band estimates per strategy option
    'infra_cost_estimates',
    // v10.5 / ADR-0072 §7 (migration 466): target plans + versioned plan documents
    'infra_plans', 'infra_plan_documents',
    // v10.5 / ADR-0072 §7 (migration 467): signed, versioned plan packages
    'infra_plan_packages',
    // V105-7-1 / ADR-0074 (migrations 468, 469): signed verification bundles + their results (tenant evidence).
    'verification_bundles', 'verification_results', 'verification_probe_results',
    // V105-7-2 (migration 470b): a plan version's spike requirements + its append-only state log
    'infra_plan_spike_requirements', 'infra_plan_state_events',
    // V108-4-4 / ADR-0085 §3 (migration 616): enabler state per workspace (append-only with supersede)
    'workspace_enablers',
    // V108-4-5 / ADR-0085 §6 (migration 619): daily per-program coverage snapshots
    'program_coverage_snapshots',
    // V108-8-21 (migration 633): the stored Setup read model (one row per workspace)
    'workspace_setup_snapshots',
    // V109-4-1 / ADR-0089 (migrations 665–666): workspace sweeps and their child collections
    'scan_runs', 'scan_run_collections',
    // V109-4-3 / 4-4 (migrations 668–669): append-only drift events + their notification keys
    'drift_events', 'drift_notification_keys',
    // V108-6-4 / ADR-0085 §5–6 (migration 626): declared production cloud accounts (AC-3 partial credit)
    'cloud_production_accounts',
    // V108-8-13 (migration 631): signed CI runs from a workflow the workspace does not trust yet (Setup "Trust it")
    'ci_identity_observations',
    // v10.9 Track 2 / ADR-0088 §1 (migrations 653–654): resource instances under inventory items + their discovery sources
    'infra_resources', 'infra_resource_sources',
    // v10.9 Track 2 / ADR-0088 §1 (migration 655): unresolved discovery clues (stored and reviewable)
    'infra_unresolved_clues',
    // v10.9 Track 2 / ADR-0088 §7 (migration 656): control applicability per program × control × resource
    'control_applicability',
    // v10.9 Track 3 / ADR-0088 §2 (migration 662): append-only observations from the slice collectors (tenant data)
    'observations',
    // v11.7 Track 3 / ADR-0112 §2 (migration 1021): confirmed content records, the input of the layer-1 content checks
    'evidence_content_records',
    // views over tenant tables (D-98-12 acceptances; legacy SoD warnings; restated assessments)
    'acceptances_canonical', 'v_sod_warnings', 'assessments_restated',
    // ADR-0047: firm tier tenant tables
    ...FIRM_TENANT_TABLES,
];
const PLANE_OF = new Map();
for (const t of CONTROL_PLANE_TABLES)
    PLANE_OF.set(t, 'control');
for (const t of TENANT_PLANE_TABLES)
    PLANE_OF.set(t, 'tenant');
/** The plane that owns `table`, or null when it is unclassified. */
export function planeOfTable(table) {
    return PLANE_OF.get(table.toLowerCase()) ?? null;
}
/** Strip comments and string literals so neither can masquerade as a table name. */
function stripNoise(sql) {
    return sql
        .replace(/--[^\n]*/g, ' ')
        .replace(/\/\*[\s\S]*?\*\//g, ' ')
        .replace(/'(?:[^']|'')*'/g, "''");
}
/**
 * The table names a statement reads or writes: the identifier after FROM /
 * JOIN / INTO / UPDATE (and `DELETE FROM`), minus CTE names and table-valued
 * functions (e.g. `json_each`). Column extracts like `EXTRACT(x FROM col)` are
 * not used in this codebase's SQL.
 */
export function tablesInSql(sql) {
    const s = stripNoise(sql);
    const ctes = new Set();
    for (const m of s.matchAll(/(?:\bWITH\s+(?:RECURSIVE\s+)?|,\s*)([A-Za-z_][A-Za-z0-9_]*)\s*(?:\([^)]*\)\s*)?AS\s*(?:NOT\s+)?(?:MATERIALIZED\s+)?\(/gi)) {
        ctes.add(m[1].toLowerCase());
    }
    const out = new Set();
    for (const m of s.matchAll(/\b(FROM|JOIN|INTO|UPDATE)\s+([A-Za-z_][A-Za-z0-9_]*)(\s*\()?/gi)) {
        const name = m[2].toLowerCase();
        // FROM/JOIN name( … ) is a table-valued function, e.g. json_each;
        // INTO name ( … ) is an INSERT's column list.
        if (m[3] && /^(FROM|JOIN)$/i.test(m[1]))
            continue;
        if (ctes.has(name))
            continue;
        if (/^(select|values|set|only|lateral)$/.test(name))
            continue;
        out.add(name);
    }
    return [...out];
}
/**
 * The workspace's active members. Services that used to JOIN `memberships`
 * onto a tenant table filter in code with this (written while the planes were
 * separate databases; still correct on one).
 */
export async function activeMemberIds(db, workspaceId) {
    const r = await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND removed_at IS NULL`, [workspaceId]);
    return new Set(r.results.map((m) => m.user_id));
}
//# sourceMappingURL=data-plane-router.js.map